import { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { getEventDetail } from "../api/eventApi";
import { showError } from "../utils/alert";
import { formatDateTime } from "../utils/dateFormat";

const LABELS = {
  summary: "Summary",
  user_first_name: "User First Name",
  user_last_name: "User Last Name",
  description: "Description",
  location: "Location",
  start: "Start",
  start_timezone: "Start Timezone",
  end: "End",
  end_timezone: "End Timezone",
  created_at: "Created At",
  last_modified: "Last Modified",
  sequence: "Sequence",
  calendar_name: "Calendar Name",
  calendar_color: "Calendar Color",
  creator_identity: "Creator Identity",
  creator_team_identity: "Creator Team Identity",
  uploaded_at: "Uploaded At",
  event_type: "Event Type",
};

const ROTATION_LABELS = {
  rotation_code: "Rotation code",
  base_airports: "Base airports",
  report_time: "Report time",
  release_time: "Release time",
  total_tl: "Total TL",
  total_bl: "Total BL",
  total_cr: "Total CR",
  total_mu: "Total MU",
  rotation_number: "Rotation #",
  base: "Base",
  days: "Days",
  credit_hours: "Credit hours",
  tafb_hours: "TAFB hours",
  layover_cities: "Layover cities",
};

const FIELD_ORDER = [
  "summary",
  "description",
  "location",
  "start",
  "start_timezone",
  "end",
  "end_timezone",
  "created_at",
  "last_modified",
  "sequence",
  "calendar_name",
  "calendar_color",
  "creator_identity",
  "creator_team_identity",
  "uploaded_at",
  "event_type",
];

function formatDateOnly(iso) {
  if (iso == null || iso === "") return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function RotationDescriptionContent({ data }) {
  const rotation = data.rotation;
  const days = data.rotation_days || [];

  return (
    <div className="border rounded p-3 bg-light">
      {!rotation && (
        <p className="text-muted mb-0 small">
          No rotation record is linked to this event.
        </p>
      )}
      {rotation && (
        <div className="mb-4">
          <h6 className="text-secondary text-uppercase small mb-2">
            Rotation detail
          </h6>
          <dl className="row mb-0 small">
            {Object.entries(ROTATION_LABELS).map(([key, label]) => {
              const v = rotation[key];
              if (v == null || v === "") return null;
              return (
                <Fragment key={key}>
                  <dt className="col-sm-4 col-md-3">{label}</dt>
                  <dd className="col-sm-8 col-md-9">
                    {key === "report_time" || key === "release_time"
                      ? formatDateTime(v)
                      : String(v)}
                  </dd>
                </Fragment>
              );
            })}
          </dl>
        </div>
      )}

      <div>
        <h6 className="text-secondary text-uppercase small mb-2">
          Flight segments (day-wise)
          {days.length > 0 && (
            <span className="text-muted fw-normal ms-1">
              — {days.length} day{days.length !== 1 ? "s" : ""}
            </span>
          )}
        </h6>
        {days.length === 0 && (
          <p className="text-muted mb-0 small">
            No duty days or flight segments are linked to this rotation.
          </p>
        )}
        {days.map((day, idx) => {
          const dd = day.duty_day;
          const dutyDate =
            dd?.duty_date || day.duty_date || null;
          const segs = day.flight_segments || [];
          return (
            <div
              key={`rot-day-${idx}-${dd?.id ?? ""}-${dutyDate ?? "na"}`}
              className="card mb-3 border"
            >
              <div className="card-header py-2 bg-white">
                <span className="badge bg-secondary me-2">
                  Day {idx + 1}
                  {days.length > 1 ? ` / ${days.length}` : ""}
                </span>
                <strong>
                  {dutyDate
                    ? formatDateOnly(
                        dutyDate.length === 10
                          ? `${dutyDate}T12:00:00`
                          : dutyDate
                      )
                    : "Date N/A"}
                </strong>
                {dd && (
                  <span className="text-muted small ms-2">
                    Day index {dd.day_index}
                    {dd.report_at && (
                      <>
                        {" "}
                        · Report {formatDateTime(dd.report_at)}
                      </>
                    )}
                    {dd.release_at && (
                      <>
                        {" "}
                        · Release {formatDateTime(dd.release_at)}
                      </>
                    )}
                  </span>
                )}
              </div>
              <div className="card-body p-0">
                {segs.length === 0 ? (
                  <p className="text-muted small mb-0 p-2">No flights this day.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped mb-0 small">
                      <thead>
                        <tr>
                          <th>Flight</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Depart</th>
                          <th>Arrive</th>
                          <th>Blk</th>
                          <th>Tail</th>
                          <th>Gates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {segs.map((s) => (
                          <tr key={s.id}>
                            <td>{s.flight_number ?? "-"}</td>
                            <td>{s.departure_airport ?? "-"}</td>
                            <td>{s.arrival_airport ?? "-"}</td>
                            <td>{formatDateTime(s.departure_at)}</td>
                            <td>{formatDateTime(s.arrival_at)}</td>
                            <td>{s.block_time ?? "-"}</td>
                            <td>{s.tail_number ?? "-"}</td>
                            <td>
                              {[s.gate_dep, s.gate_arr].filter(Boolean).join(" → ") ||
                                "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fromUserDetail = Boolean(location.state?.fromUserDetail);
  const backPath = fromUserDetail ? "/users" : "/events";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getEventDetail(id);
        setData(res.data);
      } catch (err) {
        showError("Failed to load event detail");
        navigate(backPath);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, backPath]);

  const displayValue = (key, value) => {
    if (value == null || value === "") return "-";
    if (key === "description") {
      if (data?.event_type === "rotation") {
        return <RotationDescriptionContent data={data} />;
      }
      return (
        <div className="border rounded p-2 bg-light text-break">
          {value}
        </div>
      );
    }
    if (
      key === "start" ||
      key === "end" ||
      key === "created_at" ||
      key === "last_modified" ||
      key === "uploaded_at"
    )
      return formatDateTime(value);
    if (key === "calendar_color")
      return (
        <span
          className="badge"
          style={{
            backgroundColor: value,
            color: "#fff",
          }}
        >
          {value}
        </span>
      );
    return String(value);
  };

  return (
    <AdminLayout title="Event Detail">
      {loading && <p>Loading...</p>}
      {!loading && data && (
        <div className="card shadow-sm">
          <div className="card-body">
            <h5 className="card-title mb-3">Event Details</h5>
            <dl className="row mb-0 small">
              <dt className="col-sm-3">Summary</dt>
              <dd className="col-sm-9">{data.summary ?? "-"}</dd>
              <dt className="col-sm-3">User</dt>
              <dd className="col-sm-9">
                {data.user_first_name || data.user_last_name
                  ? `${data.user_first_name || ""} ${data.user_last_name || ""}`.trim()
                  : "-"}
              </dd>
              {FIELD_ORDER.filter((key) => key !== "summary").map((key) => (
                <Fragment key={key}>
                  <dt className="col-sm-3">
                    {LABELS[key] ||
                      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </dt>
                  <dd className="col-sm-9">{displayValue(key, data[key])}</dd>
                </Fragment>
              ))}
            </dl>
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(backPath)}
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
